import type { ComponentProps } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Icon } from '../index';

import type { DraftEntry } from './state/loggerDraft';
import type { useTheme } from './theme';
import { HistoryPanel } from './HistoryPanel';

// 🕐 history bottom sheet for the timeline logger, extracted from
// TimelineLoggerScreen (slice C) to keep that screen under the 500-line
// budget. Renders null unless an entry is focused AND its historyOpen flag
// is set. Copy lands only on PENDING sets (copy_history_to_entry) — logged
// sets are protected — then the sheet closes so the new value shows on the
// card. HistoryPanel itself renders null when the slug has no past sessions.
export function TimelineHistorySheet({
  entry,
  onClose,
  onCopy,
  locale,
  unit,
  getExerciseNameBySlug,
  t,
  theme,
}: {
  entry: DraftEntry | null;
  onClose: () => void;
  onCopy: ComponentProps<typeof HistoryPanel>['onCopy'];
  locale: string;
  unit: 'kg' | 'lb';
  getExerciseNameBySlug: React.ComponentProps<
    typeof HistoryPanel
  >['getExerciseNameBySlug'];
  t: (k: string, opts?: Record<string, unknown>) => string;
  theme: ReturnType<typeof useTheme>;
}) {
  if (!entry) return null;
  const styles = makeStyles(theme);
  return (
    <Modal
      visible={entry.historyOpen === true}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.sheet} onPress={() => {}}>
          <View style={styles.handle} />
          <View style={styles.head}>
            {/* 🕐 pulled out of Text (rule: no Icon nested in Text); head View is already flexDirection:'row' gap:s.s2 */}
            <Icon name="clock" size={theme.typography.sizes.base} color={theme.palette.ink} />
            <Text style={styles.title} numberOfLines={1}>
              {getExerciseNameBySlug(entry.exerciseId, entry.exerciseName)}
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('workout.cancel', { defaultValue: 'Close' })}
              onPress={onClose}
              hitSlop={8}
              style={styles.close}
            >
              <Icon name="close" size={theme.typography.sizes.lg} color={theme.palette.ink2} />
            </Pressable>
          </View>
          {/* No flex on the ScrollView — it sizes to content (history is
              ≤4 rows), capped by the sheet's maxHeight; a flex/flexGrow
              value collapses it to zero height on iOS. */}
          <ScrollView keyboardShouldPersistTaps="handled">
            <HistoryPanel
              exerciseId={entry.exerciseId}
              onCopy={onCopy}
              locale={locale}
              unit={unit}
              theme={theme}
              getExerciseNameBySlug={getExerciseNameBySlug}
              t={t}
            />
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'flex-end',
    },
    sheet: {
      backgroundColor: p.surface,
      borderTopLeftRadius: r.r3,
      borderTopRightRadius: r.r3,
      paddingHorizontal: s.s4,
      paddingTop: s.s2,
      paddingBottom: s.s5,
      maxHeight: '75%',
    },
    handle: {
      alignSelf: 'center',
      width: 40,
      height: 4,
      borderRadius: 2,
      backgroundColor: p.borderStrong,
      marginBottom: s.s2,
    },
    head: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s.s2,
      marginBottom: s.s2,
    },
    title: {
      flex: 1,
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: '700',
    },
    close: {
      width: 36,
      height: 36,
      borderRadius: r.r2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    closeText: { color: p.ink2, fontSize: ty.sizes.lg, fontWeight: '700' },
  });
}
